import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  Users,
  Filter,
  Trash2,
  Edit3,
  FileText,
  ChevronRight,
  RotateCw,
  X,
  AlertCircle,
  Eye,
  Play,
  Pause,
  StopCircle,
  Repeat,
  History,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin, canEditOrDeleteTask, DEPARTMENTS } from "../../lib/permissions";
import AssignTaskModal from "./AssignTaskModal";
import RecurringTaskHistoryModal from "./RecurringTaskHistoryModal";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

const STATUS_OPTIONS = ["All", "Pending", "In Progress", "Completed", "Overdue"];
const RECURRING_STATUS_OPTIONS = ["All", "Active", "Paused", "Stopped"];
const FREQUENCY_OPTIONS = ["All", "Daily", "Weekly", "Monthly", "Custom"];

function formatFriendlyDate(dateStr, timeStr) {
  if (!dateStr) return "—";
  let formatted = dateStr;
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      formatted = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  } catch {}

  if (timeStr && timeStr.trim()) {
    return `${formatted}, ${timeStr.trim()}`;
  }
  return formatted;
}

function formatReportRelative(reportObj) {
  if (!reportObj) return "No reports yet";
  const dateVal = reportObj.created_at || reportObj.updated_at;
  if (!dateVal) return "Reported";
  try {
    const d = new Date(dateVal);
    const today = new Date();
    const isToday =
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear();

    if (isToday) return "Updated today";

    const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "Updated yesterday";
    if (diffDays < 7) return `Updated ${diffDays}d ago`;

    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return "Reported";
  }
}

export default function TasksReports({ user, onOpenTask }) {
  const isSuperOrAdmin = isAdmin(user);

  const [activeTab, setActiveTab] = useSavedState("tasks_reports", "activeTab", "tasks"); // "tasks" | "reports" | "recurring"
  const [tasks, setTasks] = useState([]);
  const [reportsData, setReportsData] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [totalPersonalCount, setTotalPersonalCount] = useState(0);
  const [totalTeamReportsCount, setTotalTeamReportsCount] = useState(0);
  const [totalRecurringCount, setTotalRecurringCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [scope, setScope] = useSavedState("tasks_reports", "scope", "all");
  const [search, setSearch] = useSavedState("tasks_reports", "search", "");
  const [selectedUser, setSelectedUser] = useSavedState("tasks_reports", "selectedUser", "");
  const [selectedDept, setSelectedDept] = useSavedState("tasks_reports", "selectedDept", "All");
  const [selectedStatus, setSelectedStatus] = useSavedState("tasks_reports", "selectedStatus", "All");
  const [selectedDate, setSelectedDate] = useSavedState("tasks_reports", "selectedDate", "");
  const [selectedFrequency, setSelectedFrequency] = useSavedState("tasks_reports", "selectedFrequency", "All");

  // Modals
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [historyRecurringId, setHistoryRecurringId] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [deletingRecurringTask, setDeletingRecurringTask] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Load team users for filter dropdown (if admin)
  useEffect(() => {
    if (isSuperOrAdmin) {
      api("get", "/users")
        .then((res) => {
          if (Array.isArray(res.data)) {
            setUsersList(res.data);
          }
        })
        .catch(() => {});
    }
  }, [isSuperOrAdmin]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Load personal tasks for the Tasks tab
      const taskParams = new URLSearchParams();
      if (activeTab === "tasks") {
        if (search.trim()) taskParams.append("search", search.trim());
        if (selectedStatus && selectedStatus !== "All") taskParams.append("status", selectedStatus);
        if (selectedDept && selectedDept !== "All") taskParams.append("department", selectedDept);
        if (selectedDate) taskParams.append("date", selectedDate);
      }
      if (scope) taskParams.append("scope", scope);

      const taskQs = taskParams.toString() ? `?${taskParams.toString()}` : "";
      const res = await api("get", `/tasks${taskQs}`);
      if (Array.isArray(res.data)) {
        setTasks(res.data);
        if (scope === "all" && !search.trim() && (!selectedStatus || selectedStatus === "All") && !selectedDate && (!selectedDept || selectedDept === "All")) {
          setTotalPersonalCount(res.data.length);
        }
      }

      if (scope !== "all") {
        api("get", "/tasks?scope=all")
          .then((r) => {
            if (Array.isArray(r.data)) setTotalPersonalCount(r.data.length);
          })
          .catch(() => {});
      }

      // 2. If user is Admin, load team-wide reports for the Team Reports tab
      if (isSuperOrAdmin) {
        const repParams = new URLSearchParams();
        if (activeTab === "reports") {
          if (search.trim()) repParams.append("search", search.trim());
          if (selectedStatus && selectedStatus !== "All") repParams.append("status", selectedStatus);
          if (selectedDept && selectedDept !== "All") repParams.append("department", selectedDept);
          if (selectedUser) repParams.append("user_id", selectedUser);
          if (selectedDate) repParams.append("date", selectedDate);
        }

        const repQs = repParams.toString() ? `?${repParams.toString()}` : "";
        const repRes = await api("get", `/tasks/reports${repQs}`);
        if (Array.isArray(repRes.data)) {
          setReportsData(repRes.data);
          if (!search.trim() && (!selectedStatus || selectedStatus === "All") && (!selectedDept || selectedDept === "All") && !selectedUser && !selectedDate) {
            setTotalTeamReportsCount(repRes.data.length);
          }
        }
      }

      // 3. Load Recurring Tasks
      const recParams = new URLSearchParams();
      if (activeTab === "recurring") {
        if (search.trim()) recParams.append("search", search.trim());
        if (selectedStatus && selectedStatus !== "All") recParams.append("status", selectedStatus);
        if (selectedDept && selectedDept !== "All") recParams.append("department", selectedDept);
        if (selectedFrequency && selectedFrequency !== "All") recParams.append("frequency", selectedFrequency);
      }
      const recQs = recParams.toString() ? `?${recParams.toString()}` : "";
      const recRes = await api("get", `/tasks/recurring${recQs}`);
      if (Array.isArray(recRes.data)) {
        setRecurringTasks(recRes.data);
        if (!search.trim() && (!selectedStatus || selectedStatus === "All") && (!selectedDept || selectedDept === "All")) {
          setTotalRecurringCount(recRes.data.length);
        }
      }

      // 4. Load backend summary metrics
      api("get", `/tasks/summary?scope=${scope}`)
        .then((sRes) => {
          if (sRes.data) setSummary(sRes.data);
        })
        .catch(() => {});
    } catch (err) {
      console.error("Failed to fetch tasks/reports:", err);
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [search, selectedStatus, selectedDept, selectedUser, selectedDate, selectedFrequency, scope, isSuperOrAdmin, activeTab]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const taskPagination = usePagination(tasks, 10, { sectionKey: "tasks_reports_tasks" });
  const reportPagination = usePagination(reportsData, 10, { sectionKey: "tasks_reports_reports" });
  const recurringPagination = usePagination(recurringTasks, 10, { sectionKey: "tasks_reports_recurring" });

  // Dynamic Card Metrics calculation
  const cardMetrics = useMemo(() => {
    if (activeTab === "recurring") {
      let total = recurringTasks.length;
      let active = 0;
      let paused = 0;
      let stopped = 0;
      for (const r of recurringTasks) {
        const st = (r.status || "active").toLowerCase();
        if (st === "active") active += 1;
        else if (st === "paused") paused += 1;
        else stopped += 1;
      }
      return { total, active, paused, stopped };
    }

    const isReports = activeTab === "reports";
    const backendMetrics = isReports ? summary?.team : summary?.personal;

    const dataset = isReports ? reportsData : tasks;
    let total = 0;
    let pending = 0;
    let in_progress = 0;
    let completed = 0;
    let overdue = 0;

    for (const t of dataset) {
      const st = t.status || "Pending";
      total += 1;
      if (st === "Completed") {
        completed += 1;
      } else if (st === "In Progress") {
        in_progress += 1;
      } else if (st === "Overdue" || t.is_overdue) {
        overdue += 1;
      } else {
        pending += 1;
      }
    }

    if (backendMetrics) {
      return {
        total: backendMetrics.total ?? total,
        pending: backendMetrics.pending ?? pending,
        in_progress: backendMetrics.in_progress ?? in_progress,
        completed: backendMetrics.completed ?? completed,
        overdue: backendMetrics.overdue ?? overdue,
      };
    }

    return { total, pending, in_progress, completed, overdue };
  }, [activeTab, summary, tasks, reportsData, recurringTasks]);

  const handleDeleteTask = async () => {
    if (!deletingTask) return;
    setDeleteBusy(true);
    try {
      await api("delete", `/tasks/${deletingTask.task_id}`);
      toast.success("Task deleted successfully");
      setDeletingTask(null);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete task");
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleDeleteRecurringTask = async () => {
    if (!deletingRecurringTask) return;
    setDeleteBusy(true);
    try {
      await api("delete", `/tasks/recurring/${deletingRecurringTask.recurring_task_id}`);
      toast.success("Recurring task definition deleted successfully");
      setDeletingRecurringTask(null);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete recurring task");
    } finally {
      setDeleteBusy(false);
    }
  };

  const handlePauseRecurring = async (recId) => {
    try {
      await api("patch", `/tasks/recurring/${recId}/pause`);
      toast.success("Recurring task paused");
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pause");
    }
  };

  const handleResumeRecurring = async (recId) => {
    try {
      await api("patch", `/tasks/recurring/${recId}/resume`);
      toast.success("Recurring task resumed");
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resume");
    }
  };

  const handleStopRecurring = async (recId) => {
    if (!window.confirm("Are you sure you want to stop this recurring task? No future occurrences will be generated.")) return;
    try {
      await api("patch", `/tasks/recurring/${recId}/stop`);
      toast.success("Recurring task stopped");
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop task");
    }
  };

  const getPriorityBadgeClass = (p) => {
    const pr = (p || "Normal").toLowerCase();
    if (pr === "urgent") return "badge red";
    if (pr === "high") return "badge amber";
    if (pr === "low") return "badge slate";
    return "badge blue";
  };

  const getStatusBadgeClass = (s) => {
    const st = (s || "Pending").toLowerCase();
    if (st === "completed") return "badge green";
    if (st === "in progress") return "badge blue";
    if (st === "overdue") return "badge red";
    return "badge amber";
  };

  const getRecurringStatusBadge = (st) => {
    const s = (st || "active").toLowerCase();
    if (s === "active") return { bg: "#ecfdf5", text: "#065f46", border: "#a7f3d0", label: "Active" };
    if (s === "paused") return { bg: "#fffbeb", text: "#92400e", border: "#fde68a", label: "Paused" };
    return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: "Stopped" };
  };

  const scopeOptions = [
    { key: "all", label: "All Tasks" },
    { key: "assigned_to_me", label: "Assigned to Me" },
    { key: "created_by_me", label: "Created by Me" },
  ];

  return (
    <div className="content" data-testid="tasks-reports-page">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">{isSuperOrAdmin ? "WORKSPACE · OPERATIONS" : "MY WORKSPACE"}</div>
          <h1 data-testid="page-title">{isSuperOrAdmin ? "Tasks & Reports" : "My Tasks & Reports"}</h1>
          <p className="muted">
            {isSuperOrAdmin
              ? "Manage team tasks, recurring task definitions, multi-user assignments, and daily progress reports."
              : "Tasks assigned to you, recurring task schedules, and your daily progress reports."}
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            className="outline-btn"
            onClick={loadTasks}
            title="Refresh list"
            disabled={loading}
            data-testid="refresh-tasks-btn"
          >
            <RotateCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            className="primary-btn"
            onClick={() => {
              setEditingTask(null);
              setAssignModalOpen(true);
            }}
            data-testid="assign-task-open-btn"
          >
            <Plus size={16} /> {isSuperOrAdmin ? "Assign Task" : "New Task"}
          </button>
        </div>
      </div>

      {/* Admin View Switcher (Tasks vs Team Reports vs Recurring Tasks) */}
      <div className="tabs" style={{ marginBottom: 18 }} data-testid="tasks-view-tabs">
        <button
          className={`tab ${activeTab === "tasks" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("tasks");
            setSelectedStatus("All");
            taskPagination.setCurrentPage(1);
          }}
          data-testid="tab-tasks"
        >
          Tasks ({summary?.personal?.total ?? totalPersonalCount ?? tasks.length})
        </button>
        {isSuperOrAdmin && (
          <button
            className={`tab ${activeTab === "reports" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("reports");
              setSelectedStatus("All");
              reportPagination.setCurrentPage(1);
            }}
            data-testid="tab-reports"
          >
            Team Reports ({summary?.team?.total ?? totalTeamReportsCount ?? reportsData.length})
          </button>
        )}
        <button
          className={`tab ${activeTab === "recurring" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("recurring");
            setSelectedStatus("All");
            recurringPagination.setCurrentPage(1);
          }}
          data-testid="tab-recurring"
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <RotateCw size={13} /> Recurring Tasks ({totalRecurringCount || recurringTasks.length})
          </span>
        </button>
      </div>

      {/* Dashboard Summary Cards */}
      {activeTab === "recurring" ? (
        <div
          className="metrics-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: 20 }}
          data-testid="recurring-summary-cards"
        >
          <MetricBox
            label="All Recurring"
            value={cardMetrics.total}
            tone="blue"
            subtitle="Configured definitions"
            active={selectedStatus === "All" || !selectedStatus}
            onClick={() => {
              setSelectedStatus("All");
              recurringPagination.setCurrentPage(1);
            }}
            testId="recurring-metric-total"
          />
          <MetricBox
            label="Active"
            value={cardMetrics.active}
            tone="green"
            subtitle="Generating occurrences"
            active={selectedStatus === "Active" || selectedStatus === "active"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Active" ? "All" : "Active");
              recurringPagination.setCurrentPage(1);
            }}
            testId="recurring-metric-active"
          />
          <MetricBox
            label="Paused"
            value={cardMetrics.paused}
            tone="amber"
            subtitle="Temporarily on hold"
            active={selectedStatus === "Paused" || selectedStatus === "paused"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Paused" ? "All" : "Paused");
              recurringPagination.setCurrentPage(1);
            }}
            testId="recurring-metric-paused"
          />
          <MetricBox
            label="Stopped"
            value={cardMetrics.stopped}
            tone="slate"
            subtitle="Ended schedules"
            active={selectedStatus === "Stopped" || selectedStatus === "stopped"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Stopped" ? "All" : "Stopped");
              recurringPagination.setCurrentPage(1);
            }}
            testId="recurring-metric-stopped"
          />
        </div>
      ) : (
        <div
          className="metrics-grid"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            marginBottom: 20,
          }}
          data-testid="tasks-summary-cards"
        >
          <MetricBox
            label="Total Tasks"
            value={cardMetrics.total}
            tone="blue"
            subtitle={activeTab === "reports" ? "Accessible team tasks" : "Your personal tasks"}
            active={selectedStatus === "All" || !selectedStatus}
            onClick={() => {
              setSelectedStatus("All");
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            testId="task-metric-total"
          />
          <MetricBox
            label="Pending"
            value={cardMetrics.pending}
            tone="amber"
            subtitle="Awaiting action"
            active={selectedStatus === "Pending"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Pending" ? "All" : "Pending");
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            testId="task-metric-pending"
          />
          <MetricBox
            label="In Progress"
            value={cardMetrics.in_progress}
            tone="blue"
            subtitle="Currently active"
            active={selectedStatus === "In Progress"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "In Progress" ? "All" : "In Progress");
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            testId="task-metric-in-progress"
          />
          <MetricBox
            label="Completed"
            value={cardMetrics.completed}
            tone="green"
            subtitle="Finished tasks"
            active={selectedStatus === "Completed"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Completed" ? "All" : "Completed");
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            testId="task-metric-completed"
          />
          <MetricBox
            label="Overdue"
            value={cardMetrics.overdue}
            tone="red"
            subtitle="Past due date & time"
            active={selectedStatus === "Overdue"}
            onClick={() => {
              setSelectedStatus(selectedStatus === "Overdue" ? "All" : "Overdue");
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            testId="task-metric-overdue"
          />
        </div>
      )}

      {/* Task Scope Tabs for Personal Tasks */}
      {activeTab === "tasks" && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }} data-testid="task-scope-tabs">
          {scopeOptions.map((opt) => {
            const isActive = scope === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                className="outline-btn compact"
                style={{
                  fontSize: 12,
                  padding: "4px 12px",
                  borderRadius: 20,
                  fontWeight: isActive ? 600 : 500,
                  background: isActive ? "#1e293b" : "#fff",
                  color: isActive ? "#ffffff" : "#475569",
                  borderColor: isActive ? "#1e293b" : "#cbd5e1",
                  boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setScope(opt.key);
                  taskPagination.setCurrentPage(1);
                }}
                data-testid={`scope-tab-${opt.key}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="toolbar" style={{ gap: 10, alignItems: "center", marginBottom: 20 }}>
        {/* Search */}
        <div className="search-box" style={{ flex: "1 1 240px" }}>
          <Search size={14} />
          <input
            type="text"
            placeholder={
              activeTab === "recurring"
                ? "Search recurring tasks..."
                : activeTab === "reports"
                ? "Search tasks, assignees..."
                : "Search my tasks..."
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
              recurringPagination.setCurrentPage(1);
            }}
            data-testid="task-search-input"
          />
          {search && (
            <button
              style={{ background: "none", border: 0, color: "#94a3b8", cursor: "pointer", padding: 2 }}
              onClick={() => {
                setSearch("");
                taskPagination.setCurrentPage(1);
                reportPagination.setCurrentPage(1);
                recurringPagination.setCurrentPage(1);
              }}
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* User Filter (Team Reports tab only) */}
        {isSuperOrAdmin && activeTab === "reports" && (
          <select
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              reportPagination.setCurrentPage(1);
            }}
            style={{ height: 38, minWidth: 150 }}
            data-testid="filter-user-select"
          >
            <option value="">All Team Users</option>
            {usersList.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name} {u.user_id === user?.user_id ? "(You)" : `(${u.department || u.role})`}
              </option>
            ))}
          </select>
        )}

        {/* Department Filter */}
        {(isSuperOrAdmin || activeTab === "recurring") && (
          <select
            value={selectedDept}
            onChange={(e) => {
              setSelectedDept(e.target.value);
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
              recurringPagination.setCurrentPage(1);
            }}
            style={{ height: 38, minWidth: 140 }}
            data-testid="filter-dept-select"
          >
            <option value="All">All Departments</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}

        {/* Frequency Filter (Recurring tab only) */}
        {activeTab === "recurring" && (
          <select
            value={selectedFrequency}
            onChange={(e) => {
              setSelectedFrequency(e.target.value);
              recurringPagination.setCurrentPage(1);
            }}
            style={{ height: 38, minWidth: 130 }}
            data-testid="filter-frequency-select"
          >
            {FREQUENCY_OPTIONS.map((f) => (
              <option key={f} value={f}>
                Frequency: {f}
              </option>
            ))}
          </select>
        )}

        {/* Status Filter */}
        {activeTab === "recurring" ? (
          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              recurringPagination.setCurrentPage(1);
            }}
            style={{ height: 38, minWidth: 130 }}
            data-testid="filter-recurring-status-select"
          >
            {RECURRING_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                Status: {s}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              taskPagination.setCurrentPage(1);
              reportPagination.setCurrentPage(1);
            }}
            style={{ height: 38, minWidth: 130 }}
            data-testid="filter-status-select"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                Status: {s}
              </option>
            ))}
          </select>
        )}

        {/* Date Filter (Non-recurring tabs) */}
        {activeTab !== "recurring" && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="date"
              className="dispatch-control"
              style={{ height: 38, width: 145, padding: "4px 8px" }}
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                taskPagination.setCurrentPage(1);
                reportPagination.setCurrentPage(1);
              }}
              data-testid="filter-date-input"
              title="Filter by due date"
            />
            {selectedDate && (
              <button
                className="outline-btn"
                style={{ padding: "6px 10px", height: 38 }}
                onClick={() => {
                  setSelectedDate("");
                  taskPagination.setCurrentPage(1);
                  reportPagination.setCurrentPage(1);
                }}
                title="Clear date filter"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* TAB 1: RECURRING TASKS DEFINITION & MANAGEMENT VIEW */}
      {activeTab === "recurring" ? (
        <div className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid="recurring-tasks-table">
          <div className="table-wrap">
            <table className="panel-table">
              <thead>
                <tr>
                  <th style={{ width: "26%" }}>Recurring Task</th>
                  <th style={{ width: "20%" }}>Assigned To</th>
                  <th style={{ width: "12%" }}>Department</th>
                  <th style={{ width: "16%" }}>Schedule & Next Run</th>
                  <th style={{ width: "10%" }}>Status</th>
                  <th style={{ width: "16%" }}>Occurrences</th>
                  <th style={{ width: "16%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 36, color: "#64748b" }}>
                      Loading recurring tasks...
                    </td>
                  </tr>
                )}
                {!loading && recurringTasks.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 48, color: "#64748b" }}>
                      {search || selectedStatus !== "All" || selectedDept !== "All" || selectedFrequency !== "All"
                        ? "No recurring tasks match the filter criteria."
                        : "No recurring tasks created yet. Click '+ Assign Task' and select 'Recurring Task' to configure one."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  recurringPagination.paginatedItems.map((rec) => {
                    const stStyle = getRecurringStatusBadge(rec.status);
                    return (
                      <tr
                        key={rec.recurring_task_id}
                        onClick={() => setHistoryRecurringId(rec.recurring_task_id)}
                        style={{ cursor: "pointer" }}
                        data-testid={`recurring-row-${rec.recurring_task_id}`}
                      >
                        {/* Task Title, Recurrence Badge, Company */}
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
                            <b style={{ color: "var(--navy)", fontSize: 13 }}>{rec.title}</b>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "1px 6px",
                                borderRadius: 4,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                              title={rec.recurrence_summary}
                            >
                              <RotateCw size={10} /> {rec.recurrence_summary || "Recurring"}
                            </span>
                            <span className={getPriorityBadgeClass(rec.priority)} style={{ fontSize: 9.5, padding: "2px 6px" }}>
                              {rec.priority}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span
                              style={{
                                fontSize: 9.5,
                                fontWeight: 500,
                                padding: "1px 5px",
                                borderRadius: 3,
                                background: "#f1f5f9",
                                color: "#334155",
                                border: "1px solid #cbd5e1",
                              }}
                            >
                              {rec.company_name || "Pinaki Solutions"}
                            </span>
                            <small className="mono" style={{ color: "#94a3b8", fontSize: 11 }}>
                              {rec.recurring_task_id}
                            </small>
                          </div>
                        </td>

                        {/* Assigned To */}
                        <td>
                          {rec.assignees && rec.assignees.length > 1 ? (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                                <Users size={12} color="#2563eb" />
                                <b style={{ color: "#1e293b", fontSize: 12 }}>
                                  {rec.assignees.length} Assignees
                                </b>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                {rec.assignees.slice(0, 2).map((a) => (
                                  <span
                                    key={a.user_id}
                                    style={{
                                      fontSize: 10,
                                      padding: "1px 6px",
                                      borderRadius: 4,
                                      background: "#f1f5f9",
                                      color: "#334155",
                                      border: "1px solid #cbd5e1",
                                    }}
                                  >
                                    {a.name ? a.name.split(" ")[0] : "User"}
                                  </span>
                                ))}
                                {rec.assignees.length > 2 && (
                                  <span style={{ fontSize: 10, color: "#64748b", alignSelf: "center", fontWeight: 600 }}>
                                    +{rec.assignees.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <b>{rec.assigned_to_name || (rec.assignees && rec.assignees[0]?.name) || "—"}</b>
                              <small>{rec.department || "Sales"}</small>
                            </div>
                          )}
                        </td>

                        {/* Department */}
                        <td>
                          <span style={{ fontSize: 12, color: "#334155" }}>{rec.department || "Sales"}</span>
                        </td>

                        {/* Schedule & Next Run */}
                        <td>
                          <div style={{ fontSize: 12, color: "#1e293b", fontWeight: 500, marginBottom: 2 }}>
                            {rec.status === "active" && rec.next_run_date ? (
                              <span style={{ color: "#16a34a", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <Clock size={11} />
                                Next: {formatFriendlyDate(rec.next_run_date, rec.time_of_day)}
                              </span>
                            ) : rec.status === "paused" ? (
                              <span style={{ color: "#92400e" }}>Paused</span>
                            ) : (
                              <span style={{ color: "#64748b" }}>Schedule ended</span>
                            )}
                          </div>
                          <small style={{ color: "#64748b" }}>
                            From {formatFriendlyDate(rec.start_date)}
                            {rec.end_date ? ` to ${formatFriendlyDate(rec.end_date)}` : " (Ongoing)"}
                          </small>
                        </td>

                        {/* Status */}
                        <td>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 12,
                              background: stStyle.bg,
                              color: stStyle.text,
                              border: `1px solid ${stStyle.border}`,
                            }}
                          >
                            {stStyle.label}
                          </span>
                        </td>

                        {/* Occurrence Stats */}
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div>
                              <b style={{ fontSize: 12, color: "#0f172a" }}>
                                {rec.total_occurrences || 0} Runs
                              </b>
                              <small style={{ color: "#64748b", display: "block" }}>
                                {rec.completed_occurrences || 0} done · {rec.pending_occurrences || 0} pend
                              </small>
                            </div>
                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHistoryRecurringId(rec.recurring_task_id);
                              }}
                              style={{ fontSize: 11, padding: "2px 6px", height: 24 }}
                              title="View full occurrence history"
                              data-testid={`view-history-${rec.recurring_task_id}`}
                            >
                              <History size={11} /> History
                            </button>
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            {isSuperOrAdmin && (
                              <>
                                {rec.status === "active" ? (
                                  <button
                                    className="outline-btn compact"
                                    onClick={() => handlePauseRecurring(rec.recurring_task_id)}
                                    title="Pause occurrences"
                                    style={{ padding: "0 6px", height: 26 }}
                                    data-testid={`pause-rec-${rec.recurring_task_id}`}
                                  >
                                    <Pause size={11} />
                                  </button>
                                ) : rec.status === "paused" ? (
                                  <button
                                    className="outline-btn compact"
                                    onClick={() => handleResumeRecurring(rec.recurring_task_id)}
                                    title="Resume occurrences"
                                    style={{ padding: "0 6px", height: 26 }}
                                    data-testid={`resume-rec-${rec.recurring_task_id}`}
                                  >
                                    <Play size={11} />
                                  </button>
                                ) : null}

                                <button
                                  className="outline-btn compact"
                                  onClick={() => {
                                    setEditingTask({ ...rec, task_id: null });
                                    setAssignModalOpen(true);
                                  }}
                                  title="Edit recurring task"
                                  style={{ padding: "0 6px", height: 26 }}
                                  data-testid={`edit-rec-${rec.recurring_task_id}`}
                                >
                                  <Edit3 size={11} />
                                </button>

                                <button
                                  className="outline-btn danger compact"
                                  onClick={() => setDeletingRecurringTask(rec)}
                                  title="Delete recurring task"
                                  style={{ padding: "0 6px", height: 26 }}
                                  data-testid={`delete-rec-${rec.recurring_task_id}`}
                                >
                                  <Trash2 size={11} />
                                </button>
                              </>
                            )}
                            <button
                              className="primary-btn compact"
                              onClick={() => setHistoryRecurringId(rec.recurring_task_id)}
                              style={{ height: 26, fontSize: 11, padding: "0 8px" }}
                              data-testid={`open-rec-${rec.recurring_task_id}`}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {!loading && recurringPagination.totalItems > 0 && (
            <Pagination
              currentPage={recurringPagination.currentPage}
              totalItems={recurringPagination.totalItems}
              pageSize={recurringPagination.pageSize}
              onPageChange={recurringPagination.setCurrentPage}
              onPageSizeChange={recurringPagination.setPageSize}
              itemLabel="recurring tasks"
            />
          )}
        </div>
      ) : isSuperOrAdmin && activeTab === "reports" ? (
        /* TAB 2: ADMIN TEAM REPORTS VIEW */
        <div className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid="admin-reports-table">
          <div className="table-wrap">
            <table className="panel-table">
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>User</th>
                  <th style={{ width: "25%" }}>Task</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th style={{ width: "28%" }}>Latest Report</th>
                  <th style={{ width: "15%" }}>Last Updated</th>
                  <th style={{ width: "10%", textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 32, color: "#64748b" }}>
                      Loading team reports...
                    </td>
                  </tr>
                )}
                {!loading && reportsData.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 48, color: "#64748b" }}>
                      No reports found matching criteria.
                    </td>
                  </tr>
                )}
                {!loading &&
                  reportPagination.paginatedItems.map((item) => {
                    const lastRep = item.last_report;
                    return (
                      <tr
                        key={item.task_id}
                        onClick={() => onOpenTask && onOpenTask(item.task_id)}
                        style={{ cursor: "pointer" }}
                        data-testid={`report-row-${item.task_id}`}
                      >
                        <td>
                          <b>{item.assigned_to_name || "—"}</b>
                          <small>{item.department || "General"}</small>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <b style={{ color: "var(--navy)" }}>{item.title}</b>
                            {item.is_recurring_instance && (
                              <span
                                style={{
                                  fontSize: 9.5,
                                  fontWeight: 600,
                                  padding: "1px 6px",
                                  borderRadius: 4,
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                  border: "1px solid #bfdbfe",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                                title={`Recurring: ${item.recurrence_pattern || "Recurring"}`}
                                data-testid={`report-recurring-badge-${item.task_id}`}
                              >
                                <RotateCw size={9} /> Recurring
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "1px 6px",
                                borderRadius: 4,
                                background: "#f1f5f9",
                                color: "#334155",
                                border: "1px solid #cbd5e1",
                              }}
                              data-testid={`report-company-${item.task_id}`}
                            >
                              {item.company_name || "Pinaki Solutions"}
                            </span>
                          </div>
                          <small className="mono" style={{ color: "#64748b" }}>
                            {item.task_id}
                          </small>
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(item.status)}>{item.status}</span>
                        </td>
                        <td>
                          {lastRep ? (
                            <div>
                              <span style={{ fontSize: 12.5, color: "#1e293b", display: "block" }}>
                                {lastRep.report.length > 90
                                  ? lastRep.report.slice(0, 90) + "..."
                                  : lastRep.report}
                              </span>
                              <small style={{ color: "#64748b" }}>
                                {formatFriendlyDate(lastRep.created_at || lastRep.updated_at)} by{" "}
                                {lastRep.user_name || "User"}
                              </small>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
                              No reports submitted yet
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ fontSize: 12, color: "#334155" }}>
                            {formatFriendlyDate(item.updated_at || item.created_at)}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="outline-btn compact"
                            onClick={() => onOpenTask && onOpenTask(item.task_id)}
                            data-testid={`open-task-btn-${item.task_id}`}
                          >
                            <Eye size={12} /> Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {!loading && reportPagination.totalItems > 0 && (
            <Pagination
              currentPage={reportPagination.currentPage}
              totalItems={reportPagination.totalItems}
              pageSize={reportPagination.pageSize}
              onPageChange={reportPagination.setCurrentPage}
              onPageSizeChange={reportPagination.setPageSize}
              itemLabel="reports"
            />
          )}
        </div>
      ) : (
        /* TAB 3: STANDARD TASKS LIST VIEW */
        <div className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid="tasks-table-container">
          <div className="table-wrap">
            <table className="panel-table">
              <thead>
                <tr>
                  <th style={{ width: "24%" }}>Task</th>
                  <th style={{ width: "18%" }}>Assigned To</th>
                  <th style={{ width: "15%" }}>Assigned By</th>
                  <th style={{ width: "11%" }}>Department</th>
                  <th style={{ width: "13%" }}>Due Date & Time</th>
                  <th style={{ width: "10%" }}>Status</th>
                  <th style={{ width: "15%" }}>Last Report</th>
                  <th style={{ width: "14%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 36, color: "#64748b" }}>
                      Loading tasks...
                    </td>
                  </tr>
                )}
                {!loading && tasks.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 48, color: "#64748b" }}>
                      {search || selectedStatus !== "All" || selectedDept !== "All" || selectedUser || selectedDate
                        ? "No tasks match your search filters."
                        : scope === "created_by_me"
                        ? "No tasks created by you yet."
                        : scope === "assigned_to_me"
                        ? "No tasks assigned to you. Click '+ New Task' to create a task."
                        : isSuperOrAdmin
                        ? "No tasks found. Click '+ Assign Task' to create the first one."
                        : "No tasks found."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  taskPagination.paginatedItems.map((t) => {
                    const lastRep = t.last_report;
                    return (
                      <tr
                        key={t.task_id}
                        onClick={() => onOpenTask && onOpenTask(t.task_id)}
                        style={{ cursor: "pointer" }}
                        data-testid={`task-row-${t.task_id}`}
                      >
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <b style={{ color: "var(--navy)", fontSize: 13 }}>{t.title}</b>
                            {t.is_recurring_instance && (
                              <span
                                style={{
                                  fontSize: 9.5,
                                  fontWeight: 600,
                                  padding: "1px 6px",
                                  borderRadius: 4,
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                  border: "1px solid #bfdbfe",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                                title={`Recurring Schedule: ${t.recurrence_pattern || "Recurring"}`}
                                data-testid={`task-recurring-badge-${t.task_id}`}
                              >
                                <RotateCw size={9} /> Recurring
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: 9.5,
                                fontWeight: 600,
                                padding: "2px 6px",
                                borderRadius: 4,
                                background: "#f1f5f9",
                                color: "#334155",
                                border: "1px solid #cbd5e1",
                                letterSpacing: "0.2px",
                              }}
                              title={`Company: ${t.company_name || "Pinaki Solutions"}`}
                              data-testid={`task-company-${t.task_id}`}
                            >
                              {t.company_name || "Pinaki Solutions"}
                            </span>
                            <span className={getPriorityBadgeClass(t.priority)} style={{ fontSize: 9.5, padding: "2px 6px" }}>
                              {t.priority}
                            </span>
                          </div>
                          {t.description && (
                            <small style={{ color: "#64748b", marginTop: 2, display: "block" }}>
                              {t.description.length > 70 ? t.description.slice(0, 70) + "..." : t.description}
                            </small>
                          )}
                        </td>

                        {/* Assigned To */}
                        <td>
                          {t.assignees && t.assignees.length > 1 ? (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                                <Users size={12} color="#2563eb" />
                                <b style={{ color: "#1e293b", fontSize: 12 }}>
                                  {t.assignees.length} Assignees
                                </b>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                {t.assignees.slice(0, 2).map((a) => (
                                  <span
                                    key={a.user_id || a.name}
                                    className="badge slate"
                                    style={{
                                      fontSize: 10,
                                      padding: "1px 6px",
                                      background: "#f1f5f9",
                                      color: "#334155",
                                      border: "1px solid #cbd5e1",
                                      fontWeight: 500,
                                    }}
                                    title={`${a.name} (${a.department || ""})`}
                                  >
                                    {a.name ? a.name.split(" ")[0] : "User"}
                                  </span>
                                ))}
                                {t.assignees.length > 2 && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "#64748b",
                                      alignSelf: "center",
                                      fontWeight: 600,
                                      padding: "0 2px",
                                    }}
                                    title={t.assignees.map((a) => a.name).join(", ")}
                                  >
                                    +{t.assignees.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <b>
                                {t.assigned_to_name || (t.assignees && t.assignees[0]?.name) || "—"}{" "}
                                {t.is_self_assigned && (
                                  <span style={{ fontSize: 10, color: "#64748b", fontWeight: 400 }}>(Self)</span>
                                )}
                              </b>
                              <small>{t.assigned_to_email || (t.assignees && t.assignees[0]?.email) || t.department}</small>
                            </div>
                          )}
                        </td>

                        {/* Assigned By */}
                        <td>
                          <b>
                            {t.is_self_assigned ? `${t.assigned_to_name || user?.name || "User"} (Self)` : t.assigned_by_name || "Admin"}
                          </b>
                          <small>{formatFriendlyDate(t.assigned_at || t.created_at)}</small>
                        </td>

                        <td>
                          <span style={{ fontSize: 12, color: "#334155" }}>{t.department || "Sales"}</span>
                        </td>

                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {t.status === "Overdue" && <AlertTriangle size={12} color="#dc2626" />}
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: t.status === "Overdue" ? 600 : 400,
                                color: t.status === "Overdue" ? "#dc2626" : "#334155",
                              }}
                            >
                              {formatFriendlyDate(t.due_date, t.due_time)}
                            </span>
                          </div>
                        </td>

                        <td>
                          <span className={getStatusBadgeClass(t.status)}>{t.status}</span>
                        </td>

                        <td>
                          {lastRep ? (
                            <div>
                              <b style={{ fontSize: 11.5, color: "#0f172a" }}>{formatReportRelative(lastRep)}</b>
                              <small style={{ color: "#64748b" }}>
                                {lastRep.report.length > 45 ? lastRep.report.slice(0, 45) + "..." : lastRep.report}
                              </small>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: 11.5 }}>No reports</span>
                          )}
                        </td>

                        <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            {canEditOrDeleteTask(user, t) && (
                              <>
                                <button
                                  className="outline-btn compact"
                                  onClick={() => {
                                    setEditingTask(t);
                                    setAssignModalOpen(true);
                                  }}
                                  title="Edit Task"
                                  data-testid={`edit-task-btn-${t.task_id}`}
                                >
                                  <Edit3 size={12} /> Edit
                                </button>
                                <button
                                  className="outline-btn danger compact"
                                  onClick={() => setDeletingTask(t)}
                                  title="Delete Task"
                                  data-testid={`delete-task-btn-${t.task_id}`}
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                            <button
                              className="primary-btn compact"
                              onClick={() => onOpenTask && onOpenTask(t.task_id)}
                              data-testid={`open-task-action-${t.task_id}`}
                            >
                              Open
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {!loading && taskPagination.totalItems > 0 && (
            <Pagination
              currentPage={taskPagination.currentPage}
              totalItems={taskPagination.totalItems}
              pageSize={taskPagination.pageSize}
              onPageChange={taskPagination.setCurrentPage}
              onPageSizeChange={taskPagination.setPageSize}
              itemLabel="tasks"
            />
          )}
        </div>
      )}

      {/* Assign / Edit Task Modal */}
      {assignModalOpen && (
        <AssignTaskModal
          task={editingTask}
          user={user}
          onClose={(saved) => {
            setAssignModalOpen(false);
            setEditingTask(null);
            if (saved) loadTasks();
          }}
        />
      )}

      {/* Recurring Task History Modal */}
      {historyRecurringId && (
        <RecurringTaskHistoryModal
          recurringTaskId={historyRecurringId}
          user={user}
          onClose={() => setHistoryRecurringId(null)}
          onOpenTask={onOpenTask}
          onEditRecurring={(recDoc) => {
            setEditingTask({ ...recDoc, task_id: null });
            setAssignModalOpen(true);
          }}
        />
      )}

      {/* Soft Delete Task Confirmation Modal */}
      {deletingTask && (
        <div
          className="modal-backdrop"
          data-testid="delete-confirm-backdrop"
        >
          <div
            className="modal"
            style={{ maxWidth: 440 }}
            data-testid="delete-confirm-modal"
          >
            <div className="modal-head" style={{ marginBottom: 14 }}>
              <div>
                <div className="small-label" style={{ color: "var(--red)" }}>
                  CONFIRM DELETION
                </div>
                <h2 style={{ fontSize: 18, marginTop: 4, color: "var(--navy)" }}>Delete Task</h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setDeletingTask(null)}
                data-testid="delete-confirm-close"
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.5, margin: "0 0 16px" }}>
              Are you sure you want to delete task <b>"{deletingTask.title}"</b> (<code>{deletingTask.task_id}</code>)?
            </p>
            <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 20px" }}>
              This task will be archived and removed from active task lists. Historical daily reports will remain preserved.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                className="outline-btn"
                onClick={() => setDeletingTask(null)}
                disabled={deleteBusy}
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                style={{ background: "var(--red)", borderColor: "var(--red)" }}
                onClick={handleDeleteTask}
                disabled={deleteBusy}
                data-testid="delete-confirm-btn"
              >
                {deleteBusy ? "Deleting..." : "Delete Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Soft Delete Recurring Task Definition Confirmation Modal */}
      {deletingRecurringTask && (
        <div
          className="modal-backdrop"
          data-testid="delete-rec-confirm-backdrop"
        >
          <div
            className="modal"
            style={{ maxWidth: 460 }}
            data-testid="delete-rec-confirm-modal"
          >
            <div className="modal-head" style={{ marginBottom: 14 }}>
              <div>
                <div className="small-label" style={{ color: "var(--red)" }}>
                  DELETE RECURRING TASK DEFINITION
                </div>
                <h2 style={{ fontSize: 18, marginTop: 4, color: "var(--navy)" }}>Delete Recurring Schedule</h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setDeletingRecurringTask(null)}
                data-testid="delete-rec-confirm-close"
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.5, margin: "0 0 14px" }}>
              Are you sure you want to delete the recurring task definition <b>"{deletingRecurringTask.title}"</b> (<code>{deletingRecurringTask.recurring_task_id}</code>)?
            </p>
            <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 20px", lineHeight: 1.5 }}>
              No new future occurrences will be created. All past completed task occurrences and their daily progress reports will remain safely preserved in CRM history.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                className="outline-btn"
                onClick={() => setDeletingRecurringTask(null)}
                disabled={deleteBusy}
                data-testid="delete-rec-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                style={{ background: "var(--red)", borderColor: "var(--red)" }}
                onClick={handleDeleteRecurringTask}
                disabled={deleteBusy}
                data-testid="delete-rec-confirm-btn"
              >
                {deleteBusy ? "Deleting..." : "Delete Definition"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricBox({ label, value, tone = "blue", subtitle, onClick, active, testId }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        transition: "all 0.15s ease",
        ...(active ? { outline: "2px solid var(--navy, #1e293b)", outlineOffset: "-1px", background: "#f8fafc" } : {}),
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={testId || `task-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy, #1e293b)" }}>FILTERED</span>}
      </div>
      <strong>{value ?? 0}</strong>
      {subtitle && <small>{subtitle}</small>}
    </div>
  );
}
